import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AdminLanguageProvider, useAdminT } from "@/contexts/AdminLanguageContext";
import { LogOut, LayoutDashboard, Car, Map, ClipboardList, Settings, KeyRound, FileText, Activity, BookOpen, Users, Sparkles, Languages } from "lucide-react";
import Logo from "@/components/Logo";

function LanguageSwitch() {
  const { lang, setLang, t } = useAdminT();
  return (
    <div className="flex items-center gap-1 border border-[#2A2A2A] p-0.5" data-testid="admin-lang-switch" role="group" aria-label={t("common.language")}>
      {["es", "en"].map((code) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          data-testid={`admin-lang-${code}`}
          aria-pressed={lang === code}
          className={`px-2 h-6 text-[10px] uppercase tracking-[0.18em] ${lang === code ? "bg-[#C9A55A] text-[#0C0C0C]" : "text-[#8A8A8A] hover:text-[#F5F1EB]"}`}
        >
          {code}
        </button>
      ))}
    </div>
  );
}

function LayoutInner() {
  const { user, loading, logout } = useAuth();
  const { t } = useAdminT();
  const nav = useNavigate();

  if (loading) return <div className="min-h-screen bg-[#0C0C0C] flex items-center justify-center text-[#8A8A8A]">{t("common.loading")}</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleLogout = async () => {
    try { await logout(); nav("/admin/login"); }
    catch { window.alert("No se pudo cerrar la sesión. Inténtalo de nuevo."); }
  };

  const links = [
    { to: "/admin", end: true, key: "nav.dashboard", icon: LayoutDashboard, testid: "admin-nav-dashboard" },
    { to: "/admin/vehicles", key: "nav.vehicles", icon: Car, testid: "admin-nav-vehicles" },
    { to: "/admin/routes", key: "nav.routes", icon: Map, testid: "admin-nav-routes" },
    { to: "/admin/bookings", key: "nav.bookings", icon: ClipboardList, testid: "admin-nav-bookings" },
    { to: "/admin/tracking", key: "nav.tracking", icon: Activity, testid: "admin-nav-tracking" },
    { to: "/admin/collaborators", key: "nav.collaborators", icon: Users, testid: "admin-nav-collaborators" },
    { to: "/admin/guides", key: "nav.guides", icon: BookOpen, testid: "admin-nav-guides" },
    { to: "/admin/ai-seo", key: "nav.ai_seo", icon: Sparkles, testid: "admin-nav-ai-seo" },
    { to: "/admin/settings", key: "nav.settings", icon: Settings, testid: "admin-nav-settings" },
    { to: "/admin/legal", key: "nav.legal", icon: FileText, testid: "admin-nav-legal" },
    { to: "/admin/password", key: "nav.password", icon: KeyRound, testid: "admin-nav-password" },
  ];

  return (
    <div className="min-h-screen bg-[#0C0C0C] text-[#F5F1EB] flex flex-col md:flex-row" data-testid="admin-layout">
      <Helmet>
        <meta name="robots" content="noindex, nofollow, noarchive" />
        <title>Admin — Spain Luxury Rentals</title>
      </Helmet>
      <aside className="md:w-64 md:min-h-screen border-b md:border-b-0 md:border-r border-[#2A2A2A] p-6 flex md:flex-col md:gap-6 gap-3 items-center md:items-stretch">
        <Link to="/admin" className="flex items-center gap-3">
          <Logo size={40} />
          <div className="hidden md:block">
            <div className="font-display text-[#F5F1EB] tracking-[0.16em] text-xs">SLR · ADMIN</div>
          </div>
        </Link>
        <div className="hidden md:flex md:items-center md:gap-2 md:mt-3">
          <Languages size={14} className="text-[#8A8A8A]" />
          <LanguageSwitch />
        </div>
        <nav className="hidden md:flex md:flex-col md:gap-1 md:mt-6 flex-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end}
              data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-[#2A2A2A] text-[#C9A55A]" : "text-[#F5F1EB] hover:text-[#C9A55A] hover:bg-[#1a1a1a]"}`
              }>
              <l.icon size={16} /> {t(l.key)}
            </NavLink>
          ))}
        </nav>
        <nav className="md:hidden flex gap-3 overflow-x-auto no-scrollbar flex-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`m-${l.testid}`}
              className={({ isActive }) => `text-xs uppercase tracking-[0.16em] px-2 py-1.5 whitespace-nowrap ${isActive ? "text-[#C9A55A]" : "text-[#F5F1EB]"}`}>
              {t(l.key)}
            </NavLink>
          ))}
        </nav>
        <div className="md:hidden ml-auto"><LanguageSwitch /></div>
        <button onClick={handleLogout}
          data-testid="admin-logout"
          className="hidden md:flex items-center gap-2 px-3 py-2.5 text-sm text-[#F5F1EB] hover:text-[#B8342A] mt-auto">
          <LogOut size={16} /> {t("nav.logout")}
        </button>
        <button onClick={handleLogout} aria-label={t("nav.logout")}
          data-testid="admin-logout-mobile"
          className="md:hidden text-[#F5F1EB] hover:text-[#B8342A]"><LogOut size={18}/></button>
      </aside>
      <main className="flex-1 p-6 md:p-10 max-w-5xl">
        <Outlet />
      </main>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <AdminLanguageProvider>
      <LayoutInner />
    </AdminLanguageProvider>
  );
}
